/**
 * ValidationProgress - polls /sync/validate/{id} every 2s.
 *
 * Renders only what changes per tick: overall progress bar, status-counter
 * chips, the list of currently-running operations, and any errors. No grid
 * of pending/completed tiles - those bloated the page and held no signal.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material'
import {
  Error as ErrorIcon,
} from '@mui/icons-material'

import { getSystemIcon } from '../../../../../components/SystemIcons'
import { ValidationStatus, ValidationProgressDetail } from '../types'
import { api } from '../../../../../services/api'
import { poolwerxColors } from '../../../../../theme/colors'

interface ValidationProgressProps {
  validationId: string
  onStatusUpdate: (status: ValidationStatus) => void
  onComplete: () => void
}

const POLL_INTERVAL_MS = 2000

const formatDuration = (ms: number): string => {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  return minutes > 0 ? `${minutes}m ${remainder}s` : `${remainder}s`
}

const RunningRow: React.FC<{ detail: ValidationProgressDetail }> = ({ detail }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75, px: 1.5 }}>
    <CircularProgress size={14} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {getSystemIcon(detail.system)}
    </Box>
    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0 }} noWrap>
      {detail.fp_name || detail.fp_code}
    </Typography>
    <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
      {detail.fp_code}
    </Typography>
    {detail.current_entity && (
      <>
        <Typography variant="caption" color="textSecondary">-</Typography>
        <Typography variant="caption" color="textSecondary" noWrap>
          {detail.current_entity}
        </Typography>
      </>
    )}
  </Box>
)

const ErrorRow: React.FC<{ detail: ValidationProgressDetail }> = ({ detail }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75, px: 1.5 }}>
    <ErrorIcon color="error" fontSize="small" />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {getSystemIcon(detail.system)}
    </Box>
    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0 }} noWrap>
      {detail.fp_name || detail.fp_code}
    </Typography>
    <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
      {detail.fp_code}
    </Typography>
    {detail.error_message && (
      <>
        <Typography variant="caption" color="textSecondary">-</Typography>
        <Tooltip title={detail.error_message} arrow>
          <Typography variant="caption" color="error.main" noWrap sx={{ minWidth: 0 }}>
            {detail.error_message}
          </Typography>
        </Tooltip>
      </>
    )}
  </Box>
)

const ValidationProgress: React.FC<ValidationProgressProps> = ({
  validationId,
  onStatusUpdate,
  onComplete,
}) => {
  const [status, setStatus] = useState<ValidationStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const startedAtRef = useRef<number>(Date.now())
  const completedAtRef = useRef<number | null>(null)
  const completeFiredRef = useRef(false)
  const pollRef = useRef<number | null>(null)
  // Stash callbacks in refs so the polling effect doesn't restart on every
  // parent render (inline arrow props would otherwise change identity each render).
  const onStatusUpdateRef = useRef(onStatusUpdate)
  const onCompleteRef = useRef(onComplete)
  useEffect(() => { onStatusUpdateRef.current = onStatusUpdate }, [onStatusUpdate])
  useEffect(() => { onCompleteRef.current = onComplete }, [onComplete])

  useEffect(() => {
    let cancelled = false
    startedAtRef.current = Date.now()
    completedAtRef.current = null
    completeFiredRef.current = false

    const load = async () => {
      try {
        const response: ValidationStatus = await api.get(`/sync/validate/${validationId}`)
        if (cancelled) return
        setStatus(response)
        onStatusUpdateRef.current(response)
        const isTerminal =
          response.status === 'completed' ||
          response.status === 'failed' ||
          response.status === 'cancelled'
        if (isTerminal) {
          if (completedAtRef.current === null) {
            completedAtRef.current = Date.now()
          }
          if (!completeFiredRef.current) {
            completeFiredRef.current = true
            onCompleteRef.current()
          }
          if (pollRef.current) {
            window.clearInterval(pollRef.current)
            pollRef.current = null
          }
        }
      } catch (err: any) {
        if (cancelled) return
        setError(err.response?.data?.detail || 'Failed to load validation status')
      }
    }

    load()
    pollRef.current = window.setInterval(load, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      if (pollRef.current) {
        window.clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [validationId])

  const counters = useMemo(() => {
    const c = { pending: 0, running: 0, completed: 0, error: 0, skipped: 0 }
    if (!status) return c
    status.progress.details.forEach((d) => {
      c[d.status] = (c[d.status] || 0) + 1
    })
    return c
  }, [status])

  const runningDetails = useMemo(
    () => status?.progress.details.filter((d) => d.status === 'running') ?? [],
    [status],
  )

  const errorDetails = useMemo(
    () => status?.progress.details.filter((d) => d.status === 'error') ?? [],
    [status],
  )

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    )
  }

  if (!status) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Loading validation status...</Typography>
        </Box>
      </Paper>
    )
  }

  const isTerminal =
    status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled'
  const isCancelled = status.status === 'cancelled'
  const endMs = completedAtRef.current ?? Date.now()
  const elapsed = endMs - startedAtRef.current

  return (
    <Paper sx={{ p: 0, mb: 3, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>Validation Progress</Typography>
          <Typography variant="caption" color="textSecondary">
            {formatDuration(elapsed)} - {status.progress.completed_operations}/{status.progress.total_operations} ({status.progress.overall_percentage.toFixed(0)}%)
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={status.progress.overall_percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            mb: 1.5,
            '& .MuiLinearProgress-bar': {
              backgroundColor: isTerminal ? poolwerxColors.teal : 'primary.main',
            },
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {counters.running > 0 && (
            <Chip size="small" label={`${counters.running} running`} color="primary" variant="outlined" />
          )}
          {counters.pending > 0 && (
            <Chip size="small" label={`${counters.pending} pending`} variant="outlined" />
          )}
          {counters.completed > 0 && (
            <Chip size="small" label={`${counters.completed} done`} color="success" variant="outlined" />
          )}
          {counters.error > 0 && (
            <Chip size="small" label={`${counters.error} error`} color="error" />
          )}
          {counters.skipped > 0 && (
            <Chip size="small" label={`${counters.skipped} skipped`} variant="outlined" />
          )}
        </Box>
      </Box>

      {runningDetails.length > 0 && (
        <>
          <Divider />
          <Box sx={{ py: 0.5 }}>
            {runningDetails.map((d) => (
              <RunningRow key={`${d.fp_code}-${d.system}`} detail={d} />
            ))}
          </Box>
        </>
      )}

      {errorDetails.length > 0 && (
        <>
          <Divider />
          <Box sx={{ py: 0.5 }}>
            {errorDetails.map((d) => (
              <ErrorRow key={`${d.fp_code}-${d.system}`} detail={d} />
            ))}
          </Box>
        </>
      )}

      {isTerminal && isCancelled && (
        <>
          <Divider />
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            Validation cancelled. Partial results are shown below.
          </Alert>
        </>
      )}
      {isTerminal && !isCancelled && counters.error === 0 && (
        <>
          <Divider />
          <Alert severity="success" sx={{ borderRadius: 0 }}>
            Validation complete. Review the results below.
          </Alert>
        </>
      )}
      {isTerminal && !isCancelled && counters.error > 0 && (
        <>
          <Divider />
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            Validation complete with {counters.error} {counters.error === 1 ? 'error' : 'errors'}. Review below.
          </Alert>
        </>
      )}
    </Paper>
  )
}

export default ValidationProgress
